/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { useAuth } from '@ahm/react-auth';
import { toast } from '@ahm/react-ui';
import { ActionIcon, Group, Stack, Text, rem } from '@mantine/core';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { ModalName, showModal } from '../index';

import {
  FavoriteListType,
  useGetUserProfile,
  useToggleFavoriteDriver,
} from '@/api/user';
import HeartGreyIcon from '@/assets/icons/heart-grey.svg';
import HeartRedIcon from '@/assets/icons/heart-red.svg';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export type FavoriteDriverSectionProps = {
  onlyIcon?: boolean;
};

export default function FavoriteDriverSection({
  onlyIcon = false,
}: FavoriteDriverSectionProps) {
  const { orderDetails: order } = useBookingStore((state) => state);
  const { session } = useAuth();
  const token = session?.token || '';
  const supplierId = (order as any)?.supplier_id;
  const t = useTranslations('record.favorite_driver');

  const { data: userProfile, refetch } = useGetUserProfile({
    enabled: !!token,
  });

  const isFavorited = useMemo(() => {
    return userProfile?.favorite_list?.includes(supplierId) || false;
  }, [userProfile?.favorite_list, supplierId]);

  const { mutate: toggleFavoriteMutation, isPending: isLoading } =
    useToggleFavoriteDriver({
      onSuccess: async (_, variables) => {
        await refetch();

        variables.list === FavoriteListType.BANNED
          ? toast.info({
              title: t('success_remove_title'),
              withIcon: true,
            })
          : toast.success({
              title: t('success_add_title'),
              withIcon: true,
            });
      },
      onError: (error: any) => {
        toast.error({
          title: error?.response?.data?.title || t('error_title'),
          message: error?.response?.data?.description || t('error_message'),
          withIcon: true,
          closeButton: false,
        });
      },
    });

  const handleToggleFavorite = () => {
    if (!supplierId || isLoading) return;

    const action = isFavorited
      ? FavoriteListType.BANNED
      : FavoriteListType.FAVORITE;

    const mutateFn = () => {
      toggleFavoriteMutation({
        token,
        supplier_id: supplierId,
        list: action,
      });
    };

    if (isFavorited) {
      showModal(
        ModalName.FavoriteDriverConfirm,
        {
          supplierName: (order as any)?.supplier_name || '',
          onConfirm: mutateFn,
        },
        {
          centered: true,
          withCloseButton: false,
          closeOnClickOutside: false,
          closeOnEscape: false,
          size: 'md',
          styles: {
            body: {
              padding: 0,
              background: 'white',
            },
          },
        }
      );
    } else {
      mutateFn();
    }
  };

  if (onlyIcon) {
    return (
      <ActionIcon
        variant="transparent"
        color="neutral.7"
        loading={isLoading}
        disabled={isLoading}
        onClick={handleToggleFavorite}
      >
        {isFavorited ? (
          <HeartRedIcon width={rem(24)} height={rem(24)} />
        ) : (
          <HeartGreyIcon width={rem(24)} height={rem(24)} />
        )}
      </ActionIcon>
    );
  }

  return (
    <Stack>
      <Group
        justify="center"
        align="center"
        px={rem(16)}
        py={rem(8)}
        gap={rem(4)}
        maw="fit-content"
        bg="white"
        style={{
          zIndex: 1,
          boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.10)',
          borderRadius: rem(40),
          cursor: isLoading ? 'not-allowed' : 'pointer',
        }}
        onClick={handleToggleFavorite}
      >
        <ActionIcon
          variant="transparent"
          color="neutral.7"
          loading={isLoading}
          disabled={isLoading}
        >
          {isFavorited ? (
            <HeartRedIcon width={rem(24)} height={rem(24)} />
          ) : (
            <HeartGreyIcon width={rem(24)} height={rem(24)} />
          )}
        </ActionIcon>
        <Text size="sm" fw={600} c="neutral.7">
          {isFavorited ? t('added') : t('add')}
        </Text>
      </Group>
    </Stack>
  );
}
